<template>
  <div class="exception-results-summary">
    <!-- Summary Cards Row -->
    <div class="row g-3">
      <!-- Total Locations -->
      <div :class="binarySeverity ? 'col-6 col-md-4' : 'col-6 col-md-3'">
        <div class="summary-card clickable" :class="{ 'summary-card-active': activeFilter === 'all' }" @click="$emit('filter', 'all')">
          <div class="summary-icon bg-primary bg-opacity-10">
            <i class="bi bi-geo-alt text-primary"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value">{{ totalCount }}</div>
            <div class="summary-label">{{ locationLabel }}s</div>
          </div>
        </div>
      </div>

      <!-- OK Count -->
      <div :class="binarySeverity ? 'col-6 col-md-4' : 'col-6 col-md-3'">
        <div class="summary-card clickable" :class="{ 'summary-card-active': activeFilter === 'ok' }" @click="$emit('filter', 'ok')">
          <div class="summary-icon bg-success bg-opacity-10">
            <i class="bi bi-check-circle text-success"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value text-success">{{ okCount }}</div>
            <div class="summary-label">{{ okLabel }}</div>
          </div>
        </div>
      </div>

      <!-- Warning Count (3-level mode) -->
      <div v-if="!binarySeverity" class="col-6 col-md-3">
        <div class="summary-card clickable" :class="{ 'summary-card-active': activeFilter === 'warning' }" @click="$emit('filter', 'warning')">
          <div class="summary-icon bg-warning bg-opacity-10">
            <i class="bi bi-exclamation-triangle text-warning"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value text-warning">{{ warningCount }}</div>
            <div class="summary-label">{{ warningLabel }}</div>
          </div>
        </div>
      </div>

      <!-- Danger Count (3-level mode) -->
      <div v-if="!binarySeverity" class="col-6 col-md-3">
        <div class="summary-card clickable" :class="{ 'summary-card-active': activeFilter === 'danger' }" @click="$emit('filter', 'danger')">
          <div class="summary-icon bg-danger bg-opacity-10">
            <i class="bi bi-x-circle text-danger"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value text-danger">{{ dangerCount }}</div>
            <div class="summary-label">{{ dangerLabel }}</div>
          </div>
        </div>
      </div>

      <!-- Flagged Count (binary mode) -->
      <div v-if="binarySeverity" class="col-6 col-md-4">
        <div class="summary-card clickable" :class="{ 'summary-card-active': activeFilter === 'flagged' }" @click="$emit('filter', 'flagged')">
          <div class="summary-icon bg-warning bg-opacity-10">
            <i class="bi bi-flag text-warning"></i>
          </div>
          <div class="summary-content">
            <div class="summary-value text-warning">{{ flaggedCount }}</div>
            <div class="summary-label">{{ flaggedLabel }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Progress Bar (when running) -->
    <div v-if="isRunning" class="mt-3">
      <div class="d-flex justify-content-between small mb-1">
        <span>Processing {{ locationLabel }}s...</span>
        <span>{{ completedCount }} / {{ totalCount }}</span>
      </div>
      <div class="progress" style="height: 6px;">
        <div 
          class="progress-bar progress-bar-striped progress-bar-animated" 
          role="progressbar" 
          :style="{ width: progressPercent + '%' }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExceptionResultsSummary',

  props: {
    results: {
      type: Array,
      default: () => []
    },
    isRunning: {
      type: Boolean,
      default: false
    },
    completedCount: {
      type: Number,
      default: 0
    },
    showDetails: {
      type: Boolean,
      default: true
    },
    locationLabel: {
      type: String,
      default: 'Location'
    },
    okLabel: {
      type: String,
      default: 'All Clear'
    },
    warningLabel: {
      type: String,
      default: 'Warnings'
    },
    dangerLabel: {
      type: String,
      default: 'Critical'
    },
    /** When true, merge warning+danger into single 'Flagged' count */
    binarySeverity: {
      type: Boolean,
      default: false
    },
    flaggedLabel: {
      type: String,
      default: 'Flagged'
    },
    /** Currently active filter to highlight the matching card */
    activeFilter: {
      type: String,
      default: null
    },
    nextIssueLabel: {
      type: String,
      default: 'Next Issue'
    }
  },

  emits: ['filter'],

  computed: {
    hasResults() {
      return this.results.length > 0;
    },

    totalCount() {
      return this.results.length;
    },

    okCount() {
      return this.results.filter(r => r.status === 'ok' && r.maxSeverity === 'ok').length;
    },

    warningCount() {
      return this.results.filter(r => r.status === 'ok' && r.maxSeverity === 'warning').length;
    },

    dangerCount() {
      return this.results.filter(r => r.status === 'ok' && r.maxSeverity === 'danger').length;
    },

    flaggedCount() {
      return this.results.filter(r => r.status === 'ok' && (r.maxSeverity === 'warning' || r.maxSeverity === 'danger')).length;
    },

    progressPercent() {
      if (this.totalCount === 0) return 0;
      return Math.round((this.completedCount / this.totalCount) * 100);
    },

    dateRange() {
      const dates = [];
      for (const result of this.results) {
        if (result.days) {
          for (const day of result.days) {
            if (!dates.includes(day.datetime)) {
              dates.push(day.datetime);
            }
          }
        }
      }
      
      if (dates.length === 0) return 'N/A';
      dates.sort();
      
      const formatDate = (dt) => {
        const parts = dt.split('-');
        return `${parseInt(parts[1])}/${parseInt(parts[2])}`;
      };
      
      if (dates.length === 1) return formatDate(dates[0]);
      return `${formatDate(dates[0])} - ${formatDate(dates[dates.length - 1])}`;
    },

    nextIssueDate() {
      let earliest = null;
      
      for (const result of this.results) {
        if (result.nextExceptionDate) {
          if (!earliest || result.nextExceptionDate < earliest) {
            earliest = result.nextExceptionDate;
          }
        }
      }
      
      if (!earliest) return null;
      
      const parts = earliest.split('-');
      return `${parseInt(parts[1])}/${parseInt(parts[2])}`;
    }
  }
};
</script>

<style scoped>
.summary-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.summary-card.clickable {
  cursor: pointer;
}

.summary-card.clickable:hover {
  border-color: #adb5bd;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.summary-card-active {
  border-color: #0d6efd;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
  background: #f0f6ff;
}

.summary-icon {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.summary-content {
  flex: 1;
  min-width: 0;
}

.summary-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
}

.summary-label {
  font-size: 0.8rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-card {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 0.85rem;
}

.progress {
  border-radius: 3px;
  background-color: #e9ecef;
}
</style>
